import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../utils/axiosConfig';
import lessonsData from '../../../data/lessonsData.json';
import '../../../scss/components/LessonsPage/LessonHomework.scss';
import uploadIcon from '../../../assets/upload.svg';
import botIcon from '../../../assets/bot.svg';

const LessonHomework = ({ moduleName, lessonId, updateLessonTitle }) => {
    const [, setLessonData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    // Состояние для чата с OpenAI
    const [message, setMessage] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [aiError, setAiError] = useState(null);
    // Состояние для отслеживания завершенности урока
    const [isLessonCompleted, setIsLessonCompleted] = useState(false);
    const [lessonScore, setLessonScore] = useState(null);
    // Добавляем состояние, чтобы контролировать завершение загрузки статуса урока
    const [statusChecked, setStatusChecked] = useState(false);

    useEffect(() => {
        // Находим данные урока в JSON по moduleName и lessonId
        try {
            // Преобразуем ID в строки для корректного сравнения
            const moduleIdStr = String(moduleName);
            const lessonIdStr = String(lessonId);

            const moduleData = lessonsData.modules.find(module => String(module.id) === moduleIdStr);
            if (moduleData) {
                const lesson = moduleData.lessons.find(lesson => String(lesson.id) === lessonIdStr);
                if (lesson) {
                    setLessonData(lesson);
                    // Обновляем заголовок в родительском компоненте
                    if (updateLessonTitle) {
                        updateLessonTitle(lesson.title);
                    }
                }
            }
        } catch (error) {
            console.error('Ошибка при загрузке данных урока:', error);
        } finally {
            setIsLoading(false);
        }
    }, [moduleName, lessonId, updateLessonTitle]);

    // Проверка статуса урока и загрузка истории чата при монтировании компонента
    useEffect(() => {
        const loadChatHistoryAndCheckStatus = async () => {
            try {
                console.log('Проверка статуса урока...');
                const statusResponse = await axiosInstance.post('/openai/lesson-status', {
                    moduleId: moduleName,
                    lessonId
                });

                // Проверяем, есть ли поле success в ответе, иначе предполагаем, что ответ имеет другую структуру
                if (statusResponse.data && statusResponse.data.completed !== undefined) {
                    // Структура ответа: { completed: true/false, score: number }
                    const isCompleted = statusResponse.data.completed;
                    const score = statusResponse.data.score;

                    // Устанавливаем статус урока и баллы
                    setIsLessonCompleted(Boolean(isCompleted));
                    setLessonScore(score);

                    // Если урок завершен, добавляем специальное сообщение
                    if (isCompleted) {
                        setChatHistory([
                            {
                                role: 'assistant',
                                content: `Поздравляем! Вы успешно завершили этот урок и получили ${score} баллов.`
                            }
                        ]);
                        setStatusChecked(true);
                        // Не прерываем выполнение, чтобы загрузить историю сообщений
                    }
                }

                const historyResponse = await axiosInstance.post('/chat/history', {
                    moduleId: moduleName,
                    lessonId
                });

                if (historyResponse.data.success && historyResponse.data.data) {
                    const messages = historyResponse.data.data.messages;
                    if (messages && messages.length > 0) {
                        setChatHistory(messages);

                        // Добавляем информацию о баллах в конец истории, если урок завершен
                        if (isLessonCompleted && lessonScore !== null) {
                            setChatHistory(prev => [...prev, {
                                role: 'system',
                                content: `🏆 Результат за этот урок: ${lessonScore} баллов.`
                            }]);
                        }

                        setStatusChecked(true);
                        return; // История загружена, прерываем выполнение
                    }
                }

                // Если история не загружена или пуста, и урок не завершен, устанавливаем приветственное сообщение
                if (!isLessonCompleted) {
                    setChatHistory([
                        {
                            role: 'assistant',
                            content: 'Здравствуйте! Я ваш помощник по обучению. Чем я могу вам помочь с текущим уроком?'
                        }
                    ]);
                } else if (isLessonCompleted && lessonScore !== null) {
                    // Если урок завершен, но история пуста, показываем только информацию о завершении
                    setChatHistory([
                        {
                            role: 'assistant',
                            content: `Поздравляем! Вы успешно завершили этот урок и получили ${lessonScore} баллов.`
                        }
                    ]);
                }
                setStatusChecked(true);
            } catch (error) {
                setAiError('Не удалось загрузить историю чата');
                setStatusChecked(true);
            }
        };

        loadChatHistoryAndCheckStatus();
    }, [moduleName, lessonId, isLessonCompleted, lessonScore]);

    // Функция для отправки сообщения AI
    const sendMessage = async () => {
        if (!message.trim() || isSubmitting || isLessonCompleted) return;

        // Сохраняем сообщение, чтобы восстановить в случае ошибки
        const userMessageText = message.trim();

        // Очищаем поле ввода сразу после нажатия на кнопку
        setMessage('');

        try {
            setIsSubmitting(true);
            setAiError(null);

            // Добавляем сообщение пользователя в чат
            const userMessage = { role: 'user', content: userMessageText };
            setChatHistory(prev => [...prev, userMessage]);

            let responseReceived = false;

            // Пробуем отправить через OpenAI API
            try {
                console.log('Отправка запроса к OpenAI API...');
                const response = await axiosInstance.post('/openai/response', {
                    moduleId: moduleName,
                    lessonId,
                    message: userMessageText
                });

                // Если получили успешный ответ от OpenAI API
                if (response.data.success) {
                    responseReceived = true;
                    console.log('Получен успешный ответ от OpenAI API');

                    // Добавляем ответ AI в историю чата
                    const aiMessage = { role: 'assistant', content: response.data.response };
                    setChatHistory(prev => [...prev, aiMessage]);

                    // Проверяем, был ли урок завершен после этого ответа
                    if (response.data.isCompleted) {
                        setIsLessonCompleted(true);
                        // Получаем актуальную информацию о баллах
                        try {
                            const statusResponse = await axiosInstance.post('/openai/lesson-status', {
                                moduleId: moduleName,
                                lessonId
                            });

                            if (statusResponse.data && statusResponse.data.completed !== undefined) {
                                setLessonScore(statusResponse.data.score);
                            }
                        } catch (error) {
                        }

                        // Добавляем сообщение о завершении урока
                        setChatHistory(prev => [...prev, {
                            role: 'system',
                            content: `🎉 Поздравляем! Ваше домашнее задание принято. Вы получили дополнительные баллы за этот урок.`
                        }]);

                        // Добавляем еще одно сообщение с конкретным результатом
                        if (lessonScore !== null) {
                            setChatHistory(prev => [...prev, {
                                role: 'system',
                                content: `🏆 Результат за этот урок: ${lessonScore} баллов.`
                            }]);
                        }
                    }
                } else if (response.data && response.data.isCompleted) {
                    // Если сервер вернул информацию, что урок уже завершен
                    responseReceived = true;
                    setIsLessonCompleted(true);
                    setAiError('Этот урок уже завершен');
                } else {
                    // Если ответ успешный, но не содержит данных
                }
            } catch (error) {
                // Если ошибка связана с тем, что урок уже завершен
                if (error.response && error.response.status === 403 &&
                    error.response.data && error.response.data.isCompleted) {
                    responseReceived = true;
                    setIsLessonCompleted(true);
                    setAiError('Этот урок уже завершен');
                } else {
                }
            }

            // Если уже получили ответ через OpenAI API, прерываем выполнение
            if (responseReceived) {
                return;
            }

            // Если не получили ответ через OpenAI API, пробуем использовать API чата
            try {
                const chatResponse = await axiosInstance.post('/chat/message', {
                    moduleId: moduleName,
                    lessonId,
                    message: userMessageText
                });

                if (chatResponse.data.success) {
                    // Обновляем историю чата
                    if (chatResponse.data.data && chatResponse.data.data.messages) {
                        setChatHistory(chatResponse.data.data.messages);
                    } else if (chatResponse.data.data && chatResponse.data.data.latestMessage) {
                        // Если сервер не вернул полную историю, добавляем только последнее сообщение
                        setChatHistory(prev => [...prev, {
                            role: 'assistant',
                            content: chatResponse.data.data.latestMessage
                        }]);
                    }
                } else {
                    throw new Error(chatResponse.data.message || 'Ошибка при получении ответа через API чата');
                }
            } catch (error) {
                throw error; // Пробрасываем ошибку для общей обработки
            }
        } catch (error) {
            setAiError('Не удалось получить ответ от ассистента');

            // Восстанавливаем поле ввода, если сообщение не было отправлено
            if (message.trim() === '') {
                setMessage(userMessageText);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && !isLessonCompleted) {
            e.preventDefault();
            sendMessage();
        }
    };

    if (isLoading) {
        return <div className="lesson-loading">Загрузка урока...</div>;
    }

    // Если статус урока еще не проверен, показываем загрузку
    if (!statusChecked) {
        return <div className="lesson-loading">Проверка статуса урока...</div>;
    }

    return (
        <div className="chat-assistant">
            <div className="chat-history-container">
                {chatHistory.map((msg, index) => (
                    <div key={index} className={`chat-message ${msg.role}`}>
                        {msg.role === 'assistant' && (
                            <div className="assistant-icon">
                                <img src={botIcon} alt="Assistant" />
                            </div>
                        )}
                        <div className="message-content">{msg.content}</div>
                    </div>
                ))}
                {isSubmitting && (
                    <div className="chat-message assistant">
                        <div className="assistant-icon">
                            <img src={uploadIcon} alt="Assistant" />
                        </div>
                        <div className="message-content">Печатает...</div>
                    </div>
                )}
                {aiError && (
                    <div className="chat-error">
                        {aiError}
                    </div>
                )}
            </div>

            <div className="chat-input-wrapper">
                {isLessonCompleted ? (
                    <div className="lesson-completed-message">
                        <div className="completed-icon">🏆</div>
                        <div className="completed-text">
                            <p>Урок успешно завершен!</p>
                        </div>
                    </div>
                ) : (
                    <div className="chat-input-container">
                        <textarea
                            className="chat-input"
                            placeholder="Задайте вопрос помощнику..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isSubmitting || isLessonCompleted}
                        />
                        <div className="chat-action-buttons">
                            <button
                                className="send-message-btn"
                                onClick={sendMessage}
                                disabled={isSubmitting || !message.trim() || isLessonCompleted}
                                aria-label="Отправить сообщение"
                                title="Отправить сообщение"
                            >
                                <img src={uploadIcon} alt="Отправить" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LessonHomework; 